import { HistoryButton, useDocEvents } from "../team/collab.jsx";
const { useCallback, useEffect, useRef, useState } = React;
import { dbCreateTemplate, dbDeleteTemplate, dbGetTemplate, dbListTemplates, dbSetBoard, dbSetTemplate } from "../data.jsx";
import { BoardEditor } from "../editor/BoardEditor.jsx";
import { useT } from "../i18n.jsx";
import { emptyBoard, remapIds, rwsMockupBoard, withBoardDefaults } from "../model.jsx";
import { LayoutTemplate, Loader2, Plus, Save, T, Trash2 } from "../theme.jsx";

// ============================================================= TEMPLATE =====

let templateSeedAttempted = false;

function TemplateScreen({ perms }) {
  const { t } = useT();
  const [list, setList] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [board, setBoard] = useState(null);
  const [saveState, setSaveState] = useState("saved");
  const [applyDate, setApplyDate] = useState("");
  const [applyMsg, setApplyMsg] = useState("");
  const [error, setError] = useState("");
  const saveTimer = useRef(null);

  const loadList = useCallback(async () => {
    const items = await dbListTemplates();
    // only someone who can create templates seeds the starter one
    if (items.length > 0 || templateSeedAttempted || !perms.canMaster) { setList(items); return; }
    templateSeedAttempted = true;
    try {
      const seed = { ...rwsMockupBoard(), name: t("templateSeedName") };
      const id = await dbCreateTemplate(seed);
      setList([{ id, name: seed.name, updatedAt: Date.now() }]);
    } catch (e) { setList([]); setError(`${t("seedFailed")}: ${e && e.message ? e.message : e}`); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const openTemplate = async (id) => {
    setActiveId(id);
    const v = await dbGetTemplate(id);
    setBoard(v ? withBoardDefaults(v) : emptyBoard());
    setSaveState("saved");
  };
  useDocEvents("templates", activeId, {
    onReload: () => openTemplate(activeId),
    onSaved: () => setSaveState("saved"),
    onConflict: () => { clearTimeout(saveTimer.current); setSaveState("conflict"); },
  });

  const handleChange = (next) => {
    setBoard(next);
    setSaveState("saving");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const ok = await dbSetTemplate(activeId, next);
      setSaveState((s) => (ok ? "saved" : s === "conflict" ? s : "error"));
      setList((prev) => (prev || []).map((t) => (t.id === activeId ? { ...t, name: next.name || t.name, updatedAt: Date.now() } : t)));
    }, 600);
  };

  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const createTemplate = async () => {
    setError("");
    const name = newName.trim() || t("newTemplateDefaultName");
    const newBoard = { ...emptyBoard(), name };
    try {
      const id = await dbCreateTemplate(newBoard);
      setList((prev) => [...(prev || []), { id, name, updatedAt: Date.now() }]);
      setCreating(false); setNewName("");
      setActiveId(id); setBoard(newBoard);
    } catch (e) { setError(`${t("createTemplateFailed")}: ${e && e.message ? e.message : e}`); }
  };

  const deleteTemplate = async (id) => {
    const ok = await dbDeleteTemplate(id);
    if (ok) {
      setList((prev) => (prev || []).filter((tpl) => tpl.id !== id));
      if (activeId === id) { setActiveId(null); setBoard(null); }
    } else setError(t("deleteFailed"));
    setConfirmDeleteId(null);
  };

  const applyToDate = async () => {
    if (!applyDate || !board) return;
    const ok = await dbSetBoard(applyDate, remapIds(board));
    setApplyMsg(ok ? t("appliedToDate").replace("{date}", applyDate) : t("applyFailed"));
    setTimeout(() => setApplyMsg(""), 8000);
  };

  if (activeId && board) {
    return (
      <>
        <div style={{ padding: "10px 16px", background: T.panel, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, flexWrap: "wrap" }}>
          <button className="rb-btn" onClick={() => { setActiveId(null); setBoard(null); }} style={{ background: T.panel2, border: "none", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 12, color: T.text }}>{t("back")}</button>
          <input aria-label={t("templateNamePh")} className="rb-input" value={board.name} onChange={(e) => handleChange({ ...board, name: e.target.value })}
            style={{ fontSize: 13, fontWeight: 700, border: `1px solid ${T.border}`, borderRadius: 8, padding: "6px 10px", flex: "1 1 160px", maxWidth: 320, background: T.panel2, color: T.text, textAlign: "center" }} placeholder={t("templateNamePh")} />
          <span role="status" style={{ fontSize: 12, color: saveState === "error" || saveState === "conflict" ? T.danger : T.subtext, display: "flex", alignItems: "center", gap: 4 }}>
            {saveState === "saving" ? <Loader2 size={12} style={{ animation: "rb-spin 1s linear infinite" }} /> : <Save size={12} />} {saveState === "saving" ? t("saving") : saveState === "conflict" ? t("saveConflict") : saveState === "error" ? t("applyFailed") : t("saved")}
          </span>
          <HistoryButton col="templates" id={activeId} perms={perms} />
        </div>
        <div style={{ padding: "8px 16px", background: T.panel, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, flexWrap: "wrap" }}>
          <input aria-label={t("applyTemplateBtn")} type="date" className="rb-input" value={applyDate} onChange={(e) => setApplyDate(e.target.value)} style={{ background: T.panel2, border: "none", borderRadius: 8, padding: "6px 10px", fontSize: 12.5, color: T.text }} />
          <button className="rb-btn" onClick={applyToDate} disabled={!applyDate} style={{ background: T.accent, color: T.accentText, border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: applyDate ? "pointer" : "default", opacity: applyDate ? 1 : 0.5 }}>{t("applyTemplateBtn")}</button>
          {applyMsg && <span role="status" style={{ fontSize: 12.5, fontWeight: 700, color: T.accentInk }}>{applyMsg}</span>}
        </div>
        <BoardEditor key={activeId} board={board} onChange={handleChange} perms={perms} showMeta />
      </>
    );
  }

  return (
    <div style={{ padding: 16, textAlign: "center" }}>
      {error && <div style={{ background: T.dangerBg, color: T.danger, fontSize: 12, padding: "8px 12px", borderRadius: 8, marginBottom: 12 }}>{error}</div>}
      {creating ? (
        <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", justifyContent: "center" }}>
          <input aria-label={t("newTemplateNamePh")} className="rb-input" autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") createTemplate(); if (e.key === "Escape") { setCreating(false); setNewName(""); } }}
            placeholder={t("newTemplateNamePh")} style={{ flex: "1 1 180px", maxWidth: 260, fontSize: 13, border: `1px solid ${T.accent}`, borderRadius: 10, padding: "9px 12px", background: T.panel2, color: T.text, textAlign: "center" }} />
          <button className="rb-btn" onClick={createTemplate} style={{ background: T.accent, color: T.accentText, border: "none", borderRadius: 10, padding: "9px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>{t("create")}</button>
          <button className="rb-btn" onClick={() => { setCreating(false); setNewName(""); }} style={{ background: T.panel2, border: "none", borderRadius: 10, padding: "9px 14px", fontSize: 13, cursor: "pointer", color: T.text }}>{t("cancel")}</button>
        </div>
      ) : (
        <button className="rb-btn" onClick={() => setCreating(true)} style={{ display: "flex", alignItems: "center", gap: 6, background: T.accent, color: T.accentText, border: "none", borderRadius: 10, padding: "10px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer", marginBottom: 14, marginLeft: "auto", marginRight: "auto" }}>
          <Plus size={14} /> {t("newTemplateBtn")}
        </button>
      )}
      {list === null ? <div style={{ color: T.subtext, fontSize: 13 }}>{t("loading")}</div> :
        list.length === 0 ? <div style={{ color: T.subtext, fontSize: 13 }}>{t("noTemplatesYet")}</div> :
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 10, textAlign: "center" }}>
          {list.map((tpl) => (
            <div key={tpl.id} style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: 14, padding: 14, cursor: "pointer", position: "relative" }} onClick={() => openTemplate(tpl.id)}>
              <LayoutTemplate size={16} color={T.accent} style={{ margin: "0 auto" }} />
              <div style={{ fontSize: 13, fontWeight: 700, marginTop: 6 }}>{tpl.name}</div>
              <div style={{ fontSize: 11, color: T.subtext, marginTop: 2 }}>{t("updatedOn")} {new Date(tpl.updatedAt).toLocaleDateString("th-TH")}</div>
              {confirmDeleteId === tpl.id ? (
                <div style={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 4 }} onClick={(e) => e.stopPropagation()}>
                  <button className="rb-btn" onClick={() => deleteTemplate(tpl.id)} style={{ background: T.danger, color: "#fff", border: "none", borderRadius: 6, fontSize: 11, padding: "3px 6px", cursor: "pointer" }}>{t("deleteBtn")}</button>
                  <button aria-label={t("cancel")} className="rb-btn" onClick={() => setConfirmDeleteId(null)} style={{ background: T.panel2, color: T.text, border: "none", borderRadius: 6, fontSize: 11, padding: "3px 6px", cursor: "pointer" }}>✕</button>
                </div>
              ) : (
                <button aria-label={t("deleteBtn")} className="rb-btn" onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(tpl.id); }} style={{ position: "absolute", top: 10, right: 10, background: "none", border: "none", color: T.danger, cursor: "pointer" }}><Trash2 size={13} /></button>
              )}
            </div>
          ))}
        </div>}
    </div>
  );
}

export { templateSeedAttempted, TemplateScreen };
